/**
 * Pure async DOM utilities for the tour system.
 * No React dependencies — fully testable in isolation.
 */
import { FEEDBACK_SUBMITTED_EVENT } from "./tourTypes";

export const wait = (ms: number) =>
  new Promise((resolve) => setTimeout(resolve, ms));

export const waitForCondition = async (
  check: () => boolean,
  timeoutMs: number = 600000,
  intervalMs: number = 160,
) => {
  const start = Date.now();
  while (Date.now() - start < timeoutMs) {
    if (check()) {
      return true;
    }
    await wait(intervalMs);
  }
  return false;
};

/** Rendered with a real box — rules out `display:none` responsive branches. */
export const isElementVisible = (element: Element): boolean => {
  if (!(element instanceof HTMLElement)) return element.getClientRects().length > 0;
  if (element.getClientRects().length === 0) return false;
  const style = window.getComputedStyle(element);
  return style.visibility !== "hidden" && style.opacity !== "0";
};

/**
 * First match that is actually visible. The builder mounts its mobile and
 * desktop trees at once and toggles them with breakpoint classes, so a plain
 * querySelector can hand the tour a hidden twin of the element on screen.
 */
export const queryVisible = (selector: string): Element | null => {
  const matches = document.querySelectorAll(selector);
  for (const match of matches) {
    if (isElementVisible(match)) return match;
  }
  return null;
};

export const waitForElement = (selector: string, timeoutMs: number = 8000) =>
  waitForCondition(() => Boolean(document.querySelector(selector)), timeoutMs);

export const waitForVisibleElement = (
  selector: string,
  timeoutMs: number = 8000,
) => waitForCondition(() => Boolean(queryVisible(selector)), timeoutMs);

export const waitForAnyElement = async (
  selectors: string[],
  timeoutMs: number = 600000,
) => {
  const start = Date.now();
  while (Date.now() - start < timeoutMs) {
    for (const selector of selectors) {
      if (document.querySelector(selector)) {
        return selector;
      }
    }
    await wait(160);
  }
  return null;
};

export const waitForInputValue = (
  selector: string,
  test: (value: string) => boolean,
  timeoutMs: number = 600000,
) =>
  waitForCondition(() => {
    const element = queryVisible(selector) ?? document.querySelector(selector);
    if (!element) return false;
    if (
      element instanceof HTMLInputElement ||
      element instanceof HTMLTextAreaElement
    ) {
      return test(element.value ?? "");
    }
    if (element instanceof HTMLSelectElement) {
      return test(element.value ?? "");
    }
    return false;
  }, timeoutMs);

export const waitForClick = (selector: string, timeoutMs: number = 600000) => {
  return new Promise<boolean>((resolve) => {
    let done = false;

    const cleanup = () => {
      if (done) return;
      done = true;
      document.removeEventListener("click", onClick, true);
      window.clearTimeout(timeoutId);
    };

    const onClick = (event: MouseEvent) => {
      const target = event.target as Element | null;
      if (!target) return;
      if (target.closest(selector)) {
        cleanup();
        resolve(true);
      }
    };

    const timeoutId = window.setTimeout(() => {
      cleanup();
      resolve(false);
    }, timeoutMs);

    document.addEventListener("click", onClick, true);
  });
};

export const waitForClickTarget = (
  selectors: string[],
  timeoutMs: number = 600000,
) => {
  return new Promise<string | null>((resolve) => {
    let done = false;

    const cleanup = () => {
      if (done) return;
      done = true;
      document.removeEventListener("click", onClick, true);
      window.clearTimeout(timeoutId);
    };

    const onClick = (event: MouseEvent) => {
      const target = event.target as Element | null;
      if (!target) return;

      for (const selector of selectors) {
        if (target.closest(selector)) {
          cleanup();
          resolve(selector);
          return;
        }
      }
    };

    const timeoutId = window.setTimeout(() => {
      cleanup();
      resolve(null);
    }, timeoutMs);

    document.addEventListener("click", onClick, true);
  });
};

export const waitForWindowEvent = (
  eventName: string,
  timeoutMs: number = 600000,
) => {
  return new Promise<boolean>((resolve) => {
    let done = false;

    const cleanup = () => {
      if (done) return;
      done = true;
      window.removeEventListener(eventName, onEvent as EventListener);
      window.clearTimeout(timeoutId);
    };

    const onEvent = () => {
      cleanup();
      resolve(true);
    };

    const timeoutId = window.setTimeout(() => {
      cleanup();
      resolve(false);
    }, timeoutMs);

    window.addEventListener(eventName, onEvent as EventListener, {
      once: true,
    });
  });
};

export const waitForRatingOutcome = (timeoutMs: number = 600000) => {
  return new Promise<boolean>((resolve) => {
    let done = false;

    const cleanup = () => {
      if (done) return;
      done = true;
      window.removeEventListener(FEEDBACK_SUBMITTED_EVENT, onSubmitted);
      window.clearInterval(intervalId);
      window.clearTimeout(timeoutId);
    };

    const finish = (ok: boolean) => {
      cleanup();
      resolve(ok);
    };

    const onSubmitted = () => {
      finish(true);
    };

    const intervalId = window.setInterval(() => {
      const popupOpen = Boolean(
        document.querySelector('[data-tour="rating-popup"]'),
      );
      if (!popupOpen) {
        finish(true);
      }
    }, 180);

    const timeoutId = window.setTimeout(() => {
      finish(false);
    }, timeoutMs);

    window.addEventListener(FEEDBACK_SUBMITTED_EVENT, onSubmitted);
  });
};
